<script setup lang="ts">
/**
 * Body of /how-long-should-a-resume-be.
 *
 * Same order as the resume guide: the whole answer in one short paragraph at
 * the top (the passage an AI answer engine can lift), then the decision as a
 * table, then the two follow-up questions people actually search — can it be
 * two pages, and how do I get it onto one. The tool is offered once, at the
 * end, not beside every paragraph.
 */
const { t } = useI18n()

const rows = ['student', 'early', 'experienced', 'executive', 'academic'] as const
const pageTwoRules = ['standalone', 'label', 'fill'] as const
const cuts = ['old', 'bullets', 'summary', 'skills', 'extras', 'layout'] as const
</script>

<template>
  <div class="sofax">
    <!-- The answer, before anything else on the page -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-sm">
        <div class="shell">
          <div class="mx-auto max-w-3xl rounded-[0.625rem] border-l-4 border-accent bg-surface p-[1.875rem] dark:bg-gray-800">
            <div class="mb-3 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
              {{ t('resumeLengthGuide.short.label') }}
            </div>
            <p class="text-lg">{{ t('resumeLengthGuide.short.text') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- The decision, by situation -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-bottom">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('resumeLengthGuide.table.eyebrow')"
            :title="t('resumeLengthGuide.table.title')"
            :text="t('resumeLengthGuide.table.lead')"
            width="max-w-4xl"
          />
          <div class="mx-auto max-w-4xl overflow-x-auto">
            <table class="w-full border-collapse text-left text-base">
              <thead>
                <tr>
                  <th
                    v-for="col in ['situation', 'length', 'why']"
                    :key="col"
                    scope="col"
                    class="border border-line bg-surface px-4 py-3 font-title font-semibold text-ink dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  >
                    {{ t(`resumeLengthGuide.table.head.${col}`) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rows" :key="row">
                  <th scope="row" class="border border-line px-4 py-3 align-top font-semibold text-ink dark:border-gray-700 dark:text-white">
                    {{ t(`resumeLengthGuide.table.rows.${row}.situation`) }}
                  </th>
                  <td class="whitespace-nowrap border border-line px-4 py-3 align-top font-semibold text-accent dark:border-gray-700">
                    {{ t(`resumeLengthGuide.table.rows.${row}.length`) }}
                  </td>
                  <td class="border border-line px-4 py-3 align-top dark:border-gray-700">
                    {{ t(`resumeLengthGuide.table.rows.${row}.why`) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="mx-auto mt-6 max-w-4xl text-base text-muted dark:text-gray-400">{{ t('resumeLengthGuide.table.note') }}</p>
        </div>
      </div>
    </section>

    <!-- Should it be one page: the most searched form of the question, answered under its own heading -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-bottom">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('resumeLengthGuide.onePage.eyebrow')"
            :title="t('resumeLengthGuide.onePage.title')"
            width="max-w-4xl"
          />
          <div class="mx-auto max-w-3xl">
            <p class="text-lg">{{ t('resumeLengthGuide.onePage.p1') }}</p>
            <p class="mt-6 text-lg">{{ t('resumeLengthGuide.onePage.p2') }}</p>
            <p class="mt-6 text-lg">{{ t('resumeLengthGuide.onePage.p3') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Can it be two pages -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('resumeLengthGuide.twoPages.eyebrow')"
            :title="t('resumeLengthGuide.twoPages.title')"
            width="max-w-4xl"
          />
          <div class="mx-auto max-w-3xl">
            <p class="text-lg">{{ t('resumeLengthGuide.twoPages.p1') }}</p>
            <p class="mt-6 text-lg">{{ t('resumeLengthGuide.twoPages.p2') }}</p>
          </div>

          <ul class="mx-auto mt-10 flex max-w-3xl flex-col gap-4">
            <li
              v-for="rule in pageTwoRules"
              :key="rule"
              class="rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800"
            >
              <h3 class="!text-xl">{{ t(`resumeLengthGuide.twoPages.rules.${rule}.title`) }}</h3>
              <p class="mt-3 text-base">{{ t(`resumeLengthGuide.twoPages.rules.${rule}.text`) }}</p>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Getting it onto one page: the cutting order -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('resumeLengthGuide.fit.eyebrow')"
            :title="t('resumeLengthGuide.fit.title')"
            :text="t('resumeLengthGuide.fit.lead')"
            width="max-w-4xl"
          />

          <ol class="mx-auto flex max-w-4xl flex-col gap-6">
            <li
              v-for="(cut, index) in cuts"
              :key="cut"
              class="rounded-[0.625rem] bg-surface p-[1.875rem] dark:bg-gray-800"
            >
              <div class="flex items-start gap-5">
                <span
                  class="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-ink !text-base !font-bold !text-white dark:bg-white dark:!text-ink"
                  aria-hidden="true"
                >{{ index + 1 }}</span>
                <div class="flex-1">
                  <h3 class="!text-xl md:!text-2xl">{{ t(`resumeLengthGuide.fit.items.${cut}.title`) }}</h3>
                  <p class="mt-3 text-base">{{ t(`resumeLengthGuide.fit.items.${cut}.text`) }}</p>
                </div>
              </div>
            </li>
          </ol>
        </div>
      </div>
    </section>

    <!-- How far back: the other half of "how long" -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('resumeLengthGuide.history.eyebrow')"
            :title="t('resumeLengthGuide.history.title')"
            width="max-w-4xl"
          />
          <div class="mx-auto max-w-3xl">
            <p class="text-lg">{{ t('resumeLengthGuide.history.p1') }}</p>
            <p class="mt-6 text-lg">{{ t('resumeLengthGuide.history.p2') }}</p>
            <p class="mt-6 text-lg">{{ t('resumeLengthGuide.history.p3') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Resume vs CV: why the advice differs by country -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('resumeLengthGuide.cv.eyebrow')"
            :title="t('resumeLengthGuide.cv.title')"
            width="max-w-4xl"
          />
          <div class="mx-auto max-w-3xl">
            <p class="text-lg">{{ t('resumeLengthGuide.cv.p1') }}</p>
            <p class="mt-6 text-lg">{{ t('resumeLengthGuide.cv.p2') }}</p>
            <p class="mt-6">
              <LocaleLink to="/blog/resume-vs-cv-understanding-the-differences-and-when-to-use-each" class="link">
                {{ t('resumeLengthGuide.cv.link') }} →
              </LocaleLink>
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- The one offer on the page -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <div class="mx-auto max-w-3xl text-center">
            <div class="eyebrow mb-5">{{ t('resumeLengthGuide.next.eyebrow') }}</div>
            <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('resumeLengthGuide.next.title') }}</h2>
            <p class="mt-5 text-lg">{{ t('resumeLengthGuide.next.p1') }}</p>
            <div class="mt-8">
              <SofaxBtn to="/free-cv-builder" :label="t('resumeLengthGuide.next.cta')" variant="accent" />
            </div>
            <p class="mt-6">
              <LocaleLink to="/how-to-write-a-resume" class="link">{{ t('resumeLengthGuide.next.guideLink') }} →</LocaleLink>
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
